import { useCallback, useEffect, useMemo, useState } from 'react'
import { message, Upload } from 'antd'
import { Auth } from 'aws-amplify'
import moment from 'moment'
import { API } from 'src/utils/interceptor'
import { uploadFile } from 'src/utils/uploadFile'
import { generateFormatHash } from 'src/utils/formatHash'
import {
  BULK_MAPPING_FIELDS,
  REQUIRED_BULK_MAPPING_FIELD_KEYS,
  createEmptyBulkFieldMapping,
} from './constants'
import {
  isValidationErrorMessage,
  parseValidationErrors,
} from './validationErrors'

const getErrorMessage = error => {
  const apiMessage =
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.response?.data?.errorMessage

  if (apiMessage) return apiMessage
  if (
    error?.code === 'NETWORK_ERROR' ||
    error?.message?.includes('Network Error')
  ) {
    return 'Network error: Could not connect to the server. Please try again.'
  }
  if (error?.response?.status === 500) {
    return 'Server error: Something went wrong. Please contact OMS support if this persists.'
  }
  return error?.message || 'Something went wrong. Please try again.'
}

const getUsername = async () => {
  const session = await Auth.currentAuthenticatedUser()
  return session?.attributes?.sub
}

export const useBulkPriceFileEdit = ({
  selectedPriceFileId,
  scheduledUploads = [],
  onRefreshProducts,
  onRefreshMetadata,
  onRefreshScheduledUploads,
  onScheduledSuccess,
  onAppliedSuccess,
} = {}) => {
  const [isBulkModalVisible, setIsBulkModalVisible] = useState(false)
  const [bulkFile, setBulkFile] = useState(null)
  const [bulkFileHeaders, setBulkFileHeaders] = useState([])
  const [bulkFieldMapping, setBulkFieldMapping] = useState(
    createEmptyBulkFieldMapping(),
  )
  const [validationErrors, setValidationErrors] = useState([])
  const [isValidationErrorModalVisible, setIsValidationErrorModalVisible] =
    useState(false)
  const [bulkUploadBucket, setBulkUploadBucket] = useState(null)
  const [bulkUploadKey, setBulkUploadKey] = useState(null)
  const [isMappingScanning, setIsMappingScanning] = useState(false)
  const [removeMissingProducts, setRemoveMissingProducts] = useState(false)
  const [projectedRemovals, setProjectedRemovals] = useState(null)
  const [projectedRemovalSkus, setProjectedRemovalSkus] = useState([])
  const [isDeletionListVisible, setIsDeletionListVisible] = useState(false)
  const [isFetchingDeletionSkus, setIsFetchingDeletionSkus] = useState(false)
  const [projectedNewProducts, setProjectedNewProducts] = useState(null)
  const [projectedNewProductSkus, setProjectedNewProductSkus] = useState([])
  const [isNewProductListVisible, setIsNewProductListVisible] = useState(false)
  const [isFetchingNewProductSkus, setIsFetchingNewProductSkus] =
    useState(false)
  const [isBulkApplying, setIsBulkApplying] = useState(false)
  const [bulkProgressMessage, setBulkProgressMessage] = useState('')
  const [bulkApplySummary, setBulkApplySummary] = useState(null)
  const [isGuideExpanded, setIsGuideExpanded] = useState(false)
  const [isScheduleEnabled, setIsScheduleEnabled] = useState(false)
  const [scheduledDate, setScheduledDate] = useState(null)
  const [scheduledTime, setScheduledTime] = useState(null)
  const [scheduleError, setScheduleError] = useState('')
  const [isScheduleConflictModalVisible, setIsScheduleConflictModalVisible] =
    useState(false)
  const [conflictingSchedule, setConflictingSchedule] = useState(null)
  const [hasUserTouchedMapping, setHasUserTouchedMapping] = useState(false)
  const [autoFilledFields, setAutoFilledFields] = useState(new Set())
  const [mappingLastUsedAt, setMappingLastUsedAt] = useState(null)

  const isMappingIncomplete = useMemo(
    () =>
      REQUIRED_BULK_MAPPING_FIELD_KEYS.some(
        fieldKey => !bulkFieldMapping[fieldKey],
      ),
    [bulkFieldMapping],
  )

  const resetMapping = useCallback(() => {
    setBulkFieldMapping(createEmptyBulkFieldMapping())
    setHasUserTouchedMapping(false)
    setAutoFilledFields(new Set())
    setMappingLastUsedAt(null)
  }, [])

  const resetBulkState = useCallback(() => {
    setBulkFile(null)
    setBulkFileHeaders([])
    resetMapping()
    setBulkUploadKey(null)
    setBulkUploadBucket(null)
    setRemoveMissingProducts(false)
    setProjectedRemovals(null)
    setProjectedRemovalSkus([])
    setIsDeletionListVisible(false)
    setProjectedNewProducts(null)
    setProjectedNewProductSkus([])
    setIsNewProductListVisible(false)
    setBulkApplySummary(null)
    setIsBulkApplying(false)
    setBulkProgressMessage('')
    setIsScheduleEnabled(false)
    setScheduledDate(null)
    setScheduledTime(null)
    setScheduleError('')
    setIsScheduleConflictModalVisible(false)
    setConflictingSchedule(null)
  }, [resetMapping])

  const openBulkModal = useCallback(() => {
    setIsBulkModalVisible(true)
  }, [])

  const closeBulkModal = useCallback(() => {
    setIsBulkModalVisible(false)
    resetBulkState()
  }, [resetBulkState])

  const showValidationErrors = useCallback(errorMsg => {
    const errors = parseValidationErrors(errorMsg)
    if (errors.length === 0) return false
    setValidationErrors(errors)
    setIsValidationErrorModalVisible(true)
    return true
  }, [])

  const runDeletionPreview = useCallback(
    async includeList => {
      if (
        !removeMissingProducts ||
        !bulkUploadKey ||
        !bulkUploadBucket ||
        isMappingIncomplete ||
        !selectedPriceFileId
      ) {
        return
      }
      if (includeList) setIsFetchingDeletionSkus(true)
      try {
        const username = await getUsername()
        const response = await API.post(
          'api',
          `supplier/${selectedPriceFileId}/price-file-apply`,
          {
            body: {
              fileKey: bulkUploadKey,
              bucketName: bulkUploadBucket,
              mapping: bulkFieldMapping,
              removeMissing: true,
              dryRun: true,
              includeDeletions: includeList,
              username,
            },
          },
        )
        setProjectedRemovals(response.deleted)
        if (includeList) {
          setProjectedRemovalSkus(response.deletions || [])
        } else {
          setIsDeletionListVisible(false)
          setProjectedRemovalSkus([])
        }
      } catch (error) {
        const errorMsg = getErrorMessage(error)
        if (isValidationErrorMessage(errorMsg)) {
          showValidationErrors(errorMsg)
        } else {
          message.error(errorMsg)
        }
        setProjectedRemovals(null)
        setProjectedRemovalSkus([])
        setIsDeletionListVisible(false)
      } finally {
        if (includeList) setIsFetchingDeletionSkus(false)
      }
    },
    [
      bulkFieldMapping,
      bulkUploadBucket,
      bulkUploadKey,
      isMappingIncomplete,
      removeMissingProducts,
      selectedPriceFileId,
      showValidationErrors,
    ],
  )

  const runNewProductPreview = useCallback(
    async includeList => {
      if (
        !bulkUploadKey ||
        !bulkUploadBucket ||
        isMappingIncomplete ||
        !selectedPriceFileId
      ) {
        return
      }
      if (includeList) setIsFetchingNewProductSkus(true)
      try {
        const username = await getUsername()
        const response = await API.post(
          'api',
          `supplier/${selectedPriceFileId}/price-file-apply`,
          {
            body: {
              fileKey: bulkUploadKey,
              bucketName: bulkUploadBucket,
              mapping: bulkFieldMapping,
              removeMissing: false,
              dryRun: true,
              includeNewSkus: includeList,
              username,
            },
          },
        )
        setProjectedNewProducts(response.newSkusCount || 0)
        if (includeList) {
          setProjectedNewProductSkus(response.newSkus || [])
        } else {
          setIsNewProductListVisible(false)
          setProjectedNewProductSkus([])
        }
      } catch (error) {
        const errorMsg = getErrorMessage(error)
        if (isValidationErrorMessage(errorMsg)) {
          showValidationErrors(errorMsg)
        } else {
          message.error(errorMsg)
        }
        setProjectedNewProducts(null)
        setProjectedNewProductSkus([])
        setIsNewProductListVisible(false)
      } finally {
        if (includeList) setIsFetchingNewProductSkus(false)
      }
    },
    [
      bulkFieldMapping,
      bulkUploadBucket,
      bulkUploadKey,
      isMappingIncomplete,
      selectedPriceFileId,
      showValidationErrors,
    ],
  )

  useEffect(() => {
    if (
      removeMissingProducts &&
      !isMappingScanning &&
      validationErrors.length === 0
    ) {
      runDeletionPreview(false)
    } else {
      setProjectedRemovals(null)
      setProjectedRemovalSkus([])
      setIsDeletionListVisible(false)
    }
  }, [
    removeMissingProducts,
    isMappingScanning,
    runDeletionPreview,
    validationErrors.length,
  ])

  useEffect(() => {
    if (
      !isMappingScanning &&
      bulkUploadKey &&
      bulkUploadBucket &&
      !isMappingIncomplete &&
      validationErrors.length === 0
    ) {
      runNewProductPreview(false)
    } else {
      setProjectedNewProducts(null)
      setProjectedNewProductSkus([])
      setIsNewProductListVisible(false)
    }
  }, [
    bulkUploadKey,
    bulkUploadBucket,
    isMappingIncomplete,
    isMappingScanning,
    runNewProductPreview,
    validationErrors.length,
  ])

  useEffect(() => {
    const autoFillMapping = async () => {
      if (
        !bulkFileHeaders.length ||
        isMappingScanning ||
        hasUserTouchedMapping ||
        !selectedPriceFileId
      ) {
        return
      }

      try {
        const username = await getUsername()
        if (!username) return

        const formatHash = generateFormatHash(bulkFileHeaders)
        const response = await API.get(
          'api',
          `supplier/${selectedPriceFileId}/upload-mappings`,
          {
            queryStringParameters: { formatHash, username },
          },
        )

        if (response?.data?.mapping) {
          setBulkFieldMapping(response.data.mapping)
          setMappingLastUsedAt(response.data.lastUsedAt)
          const filledFields = new Set()
          Object.keys(response.data.mapping).forEach(key => {
            if (response.data.mapping[key]) filledFields.add(key)
          })
          setAutoFilledFields(filledFields)
          message.success(
            'We recognised this file format — your mappings have been auto-filled.',
            3,
          )
        }
      } catch (error) {
        console.error('Auto-fill failed:', error)
      }
    }

    autoFillMapping()
  }, [
    bulkFileHeaders,
    hasUserTouchedMapping,
    isMappingScanning,
    selectedPriceFileId,
  ])

  const validateScheduleDateTime = useCallback((date, time) => {
    if (!date || !time) {
      return 'Please select both date and time.'
    }

    const now = moment()
    const dateStr = date.format('YYYY-MM-DD')
    const timeStr = time.format('HH:mm')
    const scheduledMoment = moment(`${dateStr} ${timeStr}`, 'YYYY-MM-DD HH:mm')

    if (scheduledMoment.isBefore(now)) {
      return 'Scheduled time must be in the future.'
    }

    const minAdvanceMinutes = 1
    const diffMinutes = scheduledMoment.diff(now, 'minutes')
    if (diffMinutes < minAdvanceMinutes) {
      return `Scheduled time must be at least ${minAdvanceMinutes} minute${
        minAdvanceMinutes > 1 ? 's' : ''
      } in the future.`
    }

    const maxAdvanceDays = 365
    const diffDays = scheduledMoment.diff(now, 'days')
    if (diffDays > maxAdvanceDays) {
      return 'Scheduled time cannot be more than 1 year in the future.'
    }

    return ''
  }, [])

  const checkForConflict = useCallback(
    priceFileId => {
      return (
        scheduledUploads.find(
          upload =>
            upload.priceFileId === priceFileId && upload.status === 'pending',
        ) || null
      )
    },
    [scheduledUploads],
  )

  const handleScheduleToggle = useCallback(checked => {
    setIsScheduleEnabled(checked)
    if (!checked) {
      setScheduledDate(null)
      setScheduledTime(null)
      setScheduleError('')
      return
    }
    const tomorrow = moment()
      .add(1, 'day')
      .hour(9)
      .minute(0)
      .second(0)
      .millisecond(0)
    setScheduledDate(moment(tomorrow))
    setScheduledTime(moment(tomorrow))
  }, [])

  const handleScheduledDateChange = useCallback(
    date => {
      setScheduledDate(date)
      setScheduleError('')
      if (date && scheduledTime) {
        setScheduleError(validateScheduleDateTime(date, scheduledTime))
      }
    },
    [scheduledTime, validateScheduleDateTime],
  )

  const handleScheduledTimeChange = useCallback(
    time => {
      setScheduledTime(time)
      setScheduleError('')
      if (scheduledDate && time) {
        setScheduleError(validateScheduleDateTime(scheduledDate, time))
      }
    },
    [scheduledDate, validateScheduleDateTime],
  )

  const handleMappingChange = useCallback((fieldKey, value) => {
    setHasUserTouchedMapping(true)
    setAutoFilledFields(prev => {
      const next = new Set(prev)
      next.delete(fieldKey)
      return next
    })
    setBulkFieldMapping(prev => ({
      ...prev,
      [fieldKey]: value,
    }))
  }, [])

  const handleRemoveMissingToggle = useCallback(e => {
    setRemoveMissingProducts(e.target.checked)
    if (!e.target.checked) {
      setProjectedRemovals(null)
      setProjectedRemovalSkus([])
      setIsDeletionListVisible(false)
    }
  }, [])

  const handleToggleDeletionList = useCallback(async () => {
    if (!projectedRemovals || projectedRemovals === 0) {
      message.info('There are no products scheduled for deletion.')
      return
    }
    if (isDeletionListVisible) {
      setIsDeletionListVisible(false)
      return
    }
    await runDeletionPreview(true)
    setIsDeletionListVisible(true)
  }, [isDeletionListVisible, projectedRemovals, runDeletionPreview])

  const handleToggleNewProductList = useCallback(async () => {
    if (!projectedNewProducts || projectedNewProducts === 0) {
      message.info('There are no new products to be created.')
      return
    }
    if (isNewProductListVisible) {
      setIsNewProductListVisible(false)
      return
    }
    await runNewProductPreview(true)
    setIsNewProductListVisible(true)
  }, [isNewProductListVisible, projectedNewProducts, runNewProductPreview])

  const applyBulkChangesInternal = useCallback(
    async ({ skipConflictCheck = false } = {}) => {
      if (!bulkUploadKey || !selectedPriceFileId) {
        message.error('Please upload a valid file before applying changes.')
        return
      }
      if (isMappingIncomplete) {
        message.warning('Please map all required fields first.')
        return
      }

      let scheduledFor = null
      if (isScheduleEnabled) {
        const err = validateScheduleDateTime(scheduledDate, scheduledTime)
        if (err) {
          setScheduleError(err)
          return
        }

        if (!skipConflictCheck) {
          const conflict = checkForConflict(selectedPriceFileId)
          if (conflict) {
            setConflictingSchedule(conflict)
            setIsScheduleConflictModalVisible(true)
            return
          }
        }

        const dateStr = scheduledDate.format('YYYY-MM-DD')
        const timeStr = scheduledTime.format('HH:mm')
        scheduledFor = `${dateStr} ${timeStr}`
      }

      try {
        setIsBulkApplying(true)
        setBulkProgressMessage(
          scheduledFor ? 'Scheduling upload...' : 'Applying updates...',
        )
        const username = await getUsername()

        const requestBody = {
          fileKey: bulkUploadKey,
          bucketName: bulkUploadBucket,
          mapping: bulkFieldMapping,
          headers: bulkFileHeaders,
          removeMissing: removeMissingProducts,
          username,
        }
        if (scheduledFor) requestBody.scheduledFor = scheduledFor

        const response = await API.post(
          'api',
          `supplier/${selectedPriceFileId}/price-file-apply`,
          {
            body: requestBody,
          },
        )

        if (scheduledFor && response?.success) {
          message.success(response.message || 'Upload scheduled successfully')
          if (typeof onRefreshScheduledUploads === 'function') {
            await onRefreshScheduledUploads(selectedPriceFileId)
          }
          if (typeof onScheduledSuccess === 'function') {
            onScheduledSuccess(response)
          }
          setBulkProgressMessage('')
          setIsBulkApplying(false)
          closeBulkModal()
          return
        }

        setBulkApplySummary({
          updated: response.updated || 0,
          created: response.created || 0,
          deleted: response.deleted || 0,
          newSkusCount: response.newSkusCount || 0,
        })
        message.success('Price file updated successfully')
        setBulkProgressMessage('Refreshing data...')

        if (typeof onRefreshProducts === 'function') {
          await onRefreshProducts(selectedPriceFileId)
        }
        if (typeof onRefreshMetadata === 'function') {
          await onRefreshMetadata(selectedPriceFileId)
        }
        if (typeof onAppliedSuccess === 'function') {
          onAppliedSuccess(response)
        }

        setBulkProgressMessage('')
        setIsBulkApplying(false)
        closeBulkModal()
      } catch (error) {
        const errorMsg = getErrorMessage(error)
        if (isValidationErrorMessage(errorMsg)) {
          showValidationErrors(errorMsg)
        } else {
          message.error(errorMsg)
        }
        setBulkProgressMessage('')
        setIsBulkApplying(false)
      }
    },
    [
      bulkFieldMapping,
      bulkFileHeaders,
      bulkUploadBucket,
      bulkUploadKey,
      checkForConflict,
      closeBulkModal,
      isMappingIncomplete,
      isScheduleEnabled,
      onAppliedSuccess,
      onRefreshMetadata,
      onRefreshProducts,
      onRefreshScheduledUploads,
      onScheduledSuccess,
      removeMissingProducts,
      scheduledDate,
      scheduledTime,
      selectedPriceFileId,
      showValidationErrors,
      validateScheduleDateTime,
    ],
  )

  const handleApplyBulkChanges = useCallback(() => {
    return applyBulkChangesInternal({ skipConflictCheck: false })
  }, [applyBulkChangesInternal])

  const handleConfirmScheduleConflict = useCallback(async () => {
    if (!conflictingSchedule) return

    try {
      const username = await getUsername()
      if (username) {
        await API.del(
          'api',
          `supplier/scheduled-uploads/${
            conflictingSchedule._id
          }?username=${encodeURIComponent(username)}`,
        )
      }
    } catch (error) {
      console.error('Failed to cancel conflicting schedule:', error)
    }

    setIsScheduleConflictModalVisible(false)
    setConflictingSchedule(null)
    return applyBulkChangesInternal({ skipConflictCheck: true })
  }, [applyBulkChangesInternal, conflictingSchedule])

  const uploadProps = useMemo(
    () => ({
      name: 'file',
      multiple: false,
      customRequest: ({ onSuccess }) => {
        onSuccess('ok')
      },
      beforeUpload: async file => {
        try {
          if (!selectedPriceFileId) {
            message.warning('Please choose a price file first.')
            return Upload.LIST_IGNORE
          }
          setBulkFile(file)
          setIsMappingScanning(true)
          setBulkApplySummary(null)
          setProjectedRemovals(null)
          setProjectedRemovalSkus([])
          setProjectedNewProducts(null)
          setProjectedNewProductSkus([])
          setIsValidationErrorModalVisible(false)
          setValidationErrors([])
          resetMapping()

          const uploadResult = await uploadFile(file)
          if (!uploadResult?.keyName) {
            throw new Error('Upload failed. Please try again.')
          }

          const username = await getUsername()
          setBulkUploadKey(uploadResult.keyName)
          setBulkUploadBucket(
            uploadResult.bucketName || 'invoices-ptotos-staging',
          )

          const response = await API.post(
            'api',
            `supplier/${selectedPriceFileId}/price-file-upload`,
            {
              body: {
                fileKey: uploadResult.keyName,
                bucketName:
                  uploadResult.bucketName || 'invoices-ptotos-staging',
                username,
              },
            },
          )

          const headers =
            response?.headers ||
            response?.data?.headers ||
            response?.body?.headers ||
            []

          setBulkFileHeaders(headers)
          setIsMappingScanning(false)
          setHasUserTouchedMapping(false)
          setAutoFilledFields(new Set())
          setMappingLastUsedAt(null)
          message.success('File uploaded. Please map your columns.')
        } catch (error) {
          message.error(getErrorMessage(error))
          setBulkFile(null)
          setBulkUploadKey(null)
          setBulkUploadBucket(null)
          setIsMappingScanning(false)
        }
        return false
      },
      onRemove: () => {
        setBulkFile(null)
        setBulkUploadKey(null)
        setBulkUploadBucket(null)
        setBulkFileHeaders([])
        setProjectedRemovals(null)
        setProjectedRemovalSkus([])
        setIsDeletionListVisible(false)
        setProjectedNewProducts(null)
        setProjectedNewProductSkus([])
        setIsNewProductListVisible(false)
        setBulkApplySummary(null)
        setIsValidationErrorModalVisible(false)
        setValidationErrors([])
        setHasUserTouchedMapping(false)
        setAutoFilledFields(new Set())
        setMappingLastUsedAt(null)
        resetMapping()
      },
      fileList: bulkFile ? [bulkFile] : [],
      showUploadList: {
        showRemoveIcon: !isBulkApplying,
        showPreviewIcon: false,
      },
    }),
    [bulkFile, isBulkApplying, resetMapping, selectedPriceFileId],
  )

  return {
    bulkMappingFields: BULK_MAPPING_FIELDS,
    isBulkModalVisible,
    openBulkModal,
    closeBulkModal,
    resetBulkState,
    bulkFile,
    bulkFileHeaders,
    bulkFieldMapping,
    isMappingScanning,
    isMappingIncomplete,
    uploadProps,
    handleMappingChange,
    validationErrors,
    isValidationErrorModalVisible,
    setIsValidationErrorModalVisible,
    autoFilledFields,
    mappingLastUsedAt,
    removeMissingProducts,
    handleRemoveMissingToggle,
    projectedRemovals,
    projectedRemovalSkus,
    isDeletionListVisible,
    isFetchingDeletionSkus,
    handleToggleDeletionList,
    projectedNewProducts,
    projectedNewProductSkus,
    isNewProductListVisible,
    isFetchingNewProductSkus,
    handleToggleNewProductList,
    isBulkApplying,
    bulkProgressMessage,
    bulkApplySummary,
    handleApplyBulkChanges,
    isGuideExpanded,
    setIsGuideExpanded,
    isScheduleEnabled,
    handleScheduleToggle,
    scheduledDate,
    handleScheduledDateChange,
    scheduledTime,
    handleScheduledTimeChange,
    scheduleError,
    isScheduleConflictModalVisible,
    setIsScheduleConflictModalVisible,
    conflictingSchedule,
    handleConfirmScheduleConflict,
    resetMapping,
  }
}

export default useBulkPriceFileEdit
